import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { MultiLocationDisplay } from '@xing-com/crate-jobs-components-multi-location-display';
import type { CompanyData, JobDetails } from '@xing-com/crate-jobs-helpers';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { NotOnSmall, OnlyOnSmall } from '@xing-com/layout-primitives';
import { mediaConfined } from '@xing-com/layout-tokens';
import { spaceL, spaceM, spaceXL, spaceXS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { CompanyImage, CompanyName } from '../../company-info';
import { Title } from '../title';

const AdditionalInfos = styled.div`
  display: flex;
  gap: ${spaceXS};
  flex-direction: column;
`;

const AdditionalInfo = styled(BodyCopy)`
  ${lineClamp(1)}
`;

const CompanyInfo = styled.div`
  display: flex;
  gap: ${spaceM};
  margin-bottom: ${spaceXL};

  @media ${mediaConfined} {
    margin-top: ${spaceL};
  }
`;

const SmallTitleContainer = styled(OnlyOnSmall)`
  margin-bottom: ${spaceXS};
`;

const Collapsible = styled.button`
  all: unset;
  cursor: pointer;
  position: relative;
`;

type Props = {
  title?: string;
  company?: CompanyData;
  industry?: string;
  locations?: JobDetails['locations'];
};

const trackingParams = 'sc_o=jobs_header_company_logo_name_click';

export const JobIntro: React.FC<Props> = ({
  title,
  company,
  industry,
  locations,
}) => {
  const [areLocationsCollapsed, setAreLocationsCollapsed] = useState(true);

  if (!company) {
    return null;
  }

  return (
    <>
      <NotOnSmall>
        <Title title={title} />
      </NotOnSmall>
      <CompanyInfo>
        <CompanyImage
          logo={company.logo}
          link={company.link}
          trackingParams={trackingParams}
        />
        <AdditionalInfos>
          <SmallTitleContainer>
            <Title title={title} />
          </SmallTitleContainer>
          <CompanyName
            name={company.name}
            link={company.link}
            trackingParams={trackingParams}
          />
          <NotOnSmall>
            <AdditionalInfo noMargin>{industry}</AdditionalInfo>
          </NotOnSmall>
          <NotOnSmall>
            <BodyCopy noMargin>
              {locations?.map((location) => location.city).join(', ')}
            </BodyCopy>
          </NotOnSmall>
          <OnlyOnSmall>
            {areLocationsCollapsed ? (
              <Collapsible onClick={() => setAreLocationsCollapsed(false)}>
                <MultiLocationDisplay lines={2} locations={locations ?? []} />
              </Collapsible>
            ) : (
              <Collapsible onClick={() => setAreLocationsCollapsed(true)}>
                <BodyCopy noMargin>
                  {locations?.map((location) => location.city).join(', ')}
                  <br />
                  <b>
                    <FormattedMessage id="JOBS_MULTILOCATION_SHOW_LESS_LABEL" />
                  </b>
                </BodyCopy>
              </Collapsible>
            )}
          </OnlyOnSmall>
        </AdditionalInfos>
      </CompanyInfo>
    </>
  );
};
